'use client'

import { ProductCarousel } from '@/components/common'
import { ProductCardSkeleton } from '@/components/common/product-card/product-item-skeleton'
import {
    useRecentProductsQuery,
    useSaveRecentProduct,
} from '@/hooks/react-query/products/use-recent-products'
import { useTranslations } from 'next-intl'
import { useEffect } from 'react'

interface RecentProductsProps {
  productId?: string
  gaTrackingValue?: string
  gaTrackingLabel?: string
}

// Export this for use in Suspense fallback
export const RecentProductsSkeleton = () => {
  return (
    <section className='bg-light px-5 py-8 lg:p-15'>
      <div className='mb-3 h-6 w-48 rounded bg-neutral-200 lg:mb-4 lg:h-10 lg:w-64' />
      <div className='mx-5 grid auto-cols-[160px] grid-flow-col gap-4 overflow-x-auto md:mx-0 md:auto-cols-fr md:grid-cols-3 lg:grid-cols-4'>
        {[...Array(4)].map((_, i) => (
          <ProductCardSkeleton key={i} />
        ))}
      </div>
    </section>
  )
}

export const RecentProducts = ({
  productId,
  gaTrackingValue,
  gaTrackingLabel,
}: RecentProductsProps) => {
  const { recentProducts, isLoading } = useRecentProductsQuery(6)
  const t = useTranslations('product_detail.title')
  //bỏ sản phẩm có id trùng với productId
  const recentProductsFiltered = recentProducts?.filter(
    (product) => product.id !== productId
  )

  const { mutateAsync: saveRecentProduct } = useSaveRecentProduct()

  useEffect(() => {
    if (!recentProducts) return

    const shouldSaveProduct =
      productId && !recentProducts?.some((product) => product.id === productId)
    if (shouldSaveProduct) {
      saveRecentProduct(productId)
    }
  }, [saveRecentProduct, productId, recentProducts])

  if (isLoading) {
    return <RecentProductsSkeleton />
  }

  if (!recentProductsFiltered) {
    return null
  }

  return (
    <section
      className='bg-light px-5 py-8 lg:p-15'
      aria-label={t('recently_viewed_products')}
    >
      <h2 className='mb-3 font-criteria text-lg font-medium uppercase leading-6.5 text-neutral-900 lg:mb-4 lg:text-[28px] lg:leading-10'>
        {t('recently_viewed_products')}
      </h2>
      <ProductCarousel
        products={recentProductsFiltered}
        gaTrackingValue={gaTrackingValue}
        gaTrackingLabel={gaTrackingLabel}
      />
    </section>
  )
}
